import React, { useState, useEffect, useCallback } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './DailyChallengeManager.css';
import { dailyChallengeAPI, puzzleAPI } from '../services/api';
const today = new Date();
const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
const thirtyDaysLater = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

const formatDate = (date) => {
  return date.toLocaleDateString('en-GB').split('/').join('-');
};

const DailyChallengeManager = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [challenges, setChallenges] = useState({});
  const [availablePuzzles, setAvailablePuzzles] = useState([]);
  const [selectedPuzzle, setSelectedPuzzle] = useState('');
  const [puzzlePattern, setPuzzlePattern] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchChallengesForDateRange = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dailyChallengeAPI.getDailyChallengesRange(
        formatDate(thirtyDaysAgo),
        formatDate(thirtyDaysLater)
      );
      const formattedChallenges = Object.entries(data).reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
      setChallenges(formattedChallenges);
    } catch (error) {
      console.error('Error fetching challenges for date range:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchAvailablePuzzles = useCallback(async () => {
    try {
      const data = await puzzleAPI.getAllPuzzles();
      const filteredPuzzles = data.filter(puzzle => 
        puzzle.board_number.startsWith('KC')
      );
      // Reverse the filtered list to show newest puzzles first
      const reversedPuzzles = filteredPuzzles.reverse();
      setAvailablePuzzles(reversedPuzzles);
    } catch (error) {
      console.error('Error fetching available puzzles:', error);
    }
  }, []);

  useEffect(() => {
    fetchAvailablePuzzles();
    fetchChallengesForDateRange();
  }, [fetchAvailablePuzzles, fetchChallengesForDateRange]);

  useEffect(() => {
    const formattedDate = formatDate(selectedDate);
    const challenge = challenges[formattedDate];
    if (challenge) {
      setSelectedPuzzle(challenge.board_number);
      setPuzzlePattern(challenge.pattern);
    } else {
      setSelectedPuzzle('');
      setPuzzlePattern(null);
    }
  }, [selectedDate, challenges]);

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const handlePuzzleSelect = (e) => {
    const puzzleId = e.target.value;
    setSelectedPuzzle(puzzleId);
    const selectedPuzzleData = availablePuzzles.find(p => p.board_number === puzzleId);
    setPuzzlePattern(selectedPuzzleData ? selectedPuzzleData.pattern : null);
  };

  const handleAssignChallenge = async () => {
    if (!selectedPuzzle) {
      alert('Please select a puzzle');
      return;
    }
  
    const formattedDate = formatDate(selectedDate);
    setIsLoading(true);
    try {
      await dailyChallengeAPI.setDailyChallenge(formattedDate, selectedPuzzle);
      alert('Challenge assigned successfully');
      await fetchChallengesForDateRange();
    } catch (error) {
      console.error('Error assigning challenge:', error);
      alert('Failed to assign challenge');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveChallenge = async () => {
    const formattedDate = formatDate(selectedDate);
    setIsLoading(true);
    try {
      await dailyChallengeAPI.removeDailyChallenge(formattedDate);
      alert('Challenge removed successfully');
      await fetchChallengesForDateRange();
      setSelectedPuzzle('');
      setPuzzlePattern(null);
    } catch (error) {
      console.error('Error removing challenge:', error);
      alert('Failed to remove challenge');
    } finally {
      setIsLoading(false);
    }
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const formattedDate = formatDate(date);
      if (date >= thirtyDaysAgo && date <= thirtyDaysLater) {
        if (challenges[formattedDate]) {
          return 'assigned-challenge';
        } else {
          return 'unassigned-challenge';
        }
      } else {
        return 'inactive-date';
      }
    }
    return null;
  };
  
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const formattedDate = formatDate(date);
      if (challenges[formattedDate]) {
        return <div className="challenge-indicator"></div>;
      }
    }
    return null;
  };

  const PuzzlePatternDisplay = ({ pattern }) => {
    if (!pattern) return null;
    
    const gridSize = pattern.length;
  
    return (
      <div 
        className={`grid grid-${gridSize}x${gridSize}`}
        style={{ 
          gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
          width: '100%',
          maxWidth: '200px',
          margin: '20px 0 0',
          aspectRatio: '1'
        }}
      >
        {pattern.flat().map((cell, index) => (
          <div 
            key={index} 
            className="grid-cell"
            style={{ 
              backgroundColor: 
                cell === 'r' ? 'red' : 
                cell === 'b' ? 'blue' : 
                cell === 'y' ? 'yellow' : 
                cell === 'x' ? 'black' :
                'transparent'
            }}
          ></div>
        ))}
      </div>
    );
  };

  return (
    <div className="daily-challenge-manager">
      {isLoading && <div className="loading-overlay"><div className="loader"></div></div>}
      <div className="calendar-container">
        <Calendar
          onChange={handleDateClick}
          value={selectedDate}
          tileContent={tileContent}
          tileClassName={tileClassName}
          minDate={thirtyDaysAgo}
          maxDate={thirtyDaysLater}
        />
      </div>
      <div className="challenge-assignment-panel">
        <h2>Assign Daily Challenge</h2>
        <p>Selected Date: {selectedDate.toDateString()}</p>
        {challenges[formatDate(selectedDate)] ? (
          <div className="assigned-challenge-info">
            <p>Assigned Challenge: {challenges[formatDate(selectedDate)].name}</p>
            <PuzzlePatternDisplay pattern={puzzlePattern} />
            <button onClick={handleRemoveChallenge} className="remove-button">Remove Challenge</button>
          </div>
        ) : (
          <div className="assign-challenge-form">
            <select
              value={selectedPuzzle}
              onChange={handlePuzzleSelect}
            >
              <option value="">Select a puzzle</option>
              {availablePuzzles.map((puzzle) => (
                <option key={puzzle.board_number} value={puzzle.board_number}>
                  {puzzle.name} ({puzzle.board_number})
                </option>
              ))}
            </select>
            <button onClick={handleAssignChallenge} className="assign-button">Assign Challenge</button>
            {puzzlePattern && <PuzzlePatternDisplay pattern={puzzlePattern} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyChallengeManager;
